import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsappFAB from "@/components/WhatsappFAB";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import TawkChat from "@/components/TawkChat";
import ErpBadge from "@/components/ErpBadge";
import SEO from "@/components/SEO";
import JsonLd, { productSchema } from "@/components/JsonLd";
import ProductSlides from "@/components/ProductSlides";
import RoiCalculator from "@/components/RoiCalculator";
import ProductTestimonials from "@/components/ProductTestimonials";
import ProductComparison from "@/components/ProductComparison";
import { useCurrency } from "@/context/CurrencyContext";
import { useI18n } from "@/context/LangContext";
import { ArrowLeft, Check, PlayCircle, WhatsappLogo, X, ShieldCheck, Compass, CalendarCheck, CurrencyDollar, Rocket } from "@phosphor-icons/react";

export default function ProductDetail({ overrideSlug, prettySlug }) {
  const params = useParams();
  const slug = overrideSlug || params.slug;
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [err, setErr] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [demoUnlocked, setDemoUnlocked] = useState(false);
  const { formatFromAED } = useCurrency();
  const { t } = useI18n();

  useEffect(() => {
    api.get(`/products/slug/${slug}`).then((r) => setProduct(r.data)).catch((e) => setErr(e?.response?.status || "err"));
    const stored = JSON.parse(localStorage.getItem("maxine_demo_tokens") || "{}");
    if (stored[slug]) {
      api.get("/demo-verify", { params: { token: stored[slug], product_slug: slug } })
        .then(() => setDemoUnlocked(true))
        .catch(() => { const s = { ...stored }; delete s[slug]; localStorage.setItem("maxine_demo_tokens", JSON.stringify(s)); });
    }
    if (searchParams.get("demo") === "1") setGateOpen(true);
  }, [slug, searchParams]);

  const onUnlocked = () => { setGateOpen(false); setDemoUnlocked(true); nav(`/products/${slug}/demo`); };

  if (err === 404) return <NotFound />;
  if (!product) return <div className="min-h-screen bg-white" />;

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="min-h-screen bg-white">
      <SEO title={product.title} description={product.tagline || product.body_md?.slice(0, 140)} image={product.image_url} />
      <JsonLd data={productSchema(product, baseUrl)} />
      <Nav />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="container-x">
          <Link to="/#products" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-500 hover:text-[color:var(--brand)]">
            <ArrowLeft size={14} weight="bold" /> {t("product.back")}
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="font-mono text-[10px] uppercase tracking-widest bg-[color:var(--brand)]/10 text-[color:var(--brand)] px-2 py-1">{product.industry}</span>
                {(product.industries || []).slice(0, 3).map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-widest border border-[color:var(--line)] px-2 py-1 text-neutral-600">{t}</span>
                ))}
              </div>
              <div className="mb-4"><ErpBadge /></div>
              <h1 className="font-display font-black text-4xl md:text-6xl leading-[1.02] tracking-tight">{product.title}</h1>
              <p className="mt-4 text-xl md:text-2xl text-neutral-700 leading-snug">{product.tagline}</p>
              {product.price_aed_monthly > 0 && demoUnlocked && (
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">From</span>
                  <span className="font-display font-black text-3xl text-[color:var(--brand)]">{formatFromAED(product.price_aed_monthly)}</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">/ month · or license outright</span>
                </div>
              )}
              {product.price_aed_monthly > 0 && !demoUnlocked && (
                <div className="mt-6 font-mono text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[color:var(--brand)]" /> {t("product.pricegate")}
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-3">
                {product.demo_available !== false && (
                  <button data-testid="product-request-demo" onClick={() => setGateOpen(true)} className="btn-brand">
                    <PlayCircle size={16} weight="bold" /> {demoUnlocked ? "Open live demo" : t("cta.demo")}
                  </button>
                )}
                {demoUnlocked && (
                  <Link to={`/products/${slug}/tour`} data-testid="product-open-tour" className="btn-ghost">
                    <Compass size={16} weight="bold" /> {t("product.tour")}
                  </Link>
                )}
                <a href="#roi" data-testid="product-roi-jump" className="btn-ghost">
                  <CurrencyDollar size={16} weight="bold" /> {t("cta.pricing")}
                </a>
                <a href="#contact-cta" data-testid="product-consult" className="btn-ghost">
                  <CalendarCheck size={16} weight="bold" /> {t("cta.consult")}
                </a>
              </div>
              {product.body_md && (
                <div className="prose-md mt-10 text-neutral-800">
                  <ReactMarkdown>{product.body_md}</ReactMarkdown>
                </div>
              )}
            </div>

            <aside className="lg:col-span-5">
              <div className="border border-[color:var(--ink)] p-6">
                <div className="eyebrow">{t("product.inside")}</div>
                <ul className="mt-4 space-y-3">
                  {(product.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-neutral-700">
                      <Check size={16} weight="bold" className="text-[color:var(--brand)] mt-1" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 border border-[color:var(--line)] p-6 bg-[color:var(--paper-alt)]">
                <ShieldCheck size={20} weight="bold" className="text-[color:var(--brand)]" />
                <p className="mt-3 text-sm text-neutral-700 leading-relaxed">{t("product.host")}</p>
              </div>
              {demoUnlocked && (
                <Link to={`/products/${slug}/demo`} data-testid="product-open-demo" className="mt-4 btn-brand justify-center w-full text-sm">
                  <PlayCircle size={14} weight="bold" /> Open unlocked demo →
                </Link>
              )}
            </aside>
          </div>

          <ProductSlides product={product} />

          <ProductComparison productTitle={product.title} />

          <ProductTestimonials productSlug={slug} />

          <RoiCalculator productTitle={product.title} />

          {/* Bottom CTA */}
          <div id="contact-cta" className="mt-16 border-2 border-[color:var(--brand)] bg-[color:var(--brand)]/5 p-8 md:p-14 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="eyebrow">{t("product.ready")}</div>
              <h2 className="font-display font-black text-3xl md:text-4xl leading-tight mt-2">{t("product.readyTitle", { product: product.title })}</h2>
              <p className="mt-3 text-sm md:text-base text-neutral-700 max-w-md">
                {t("product.readySub")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-start md:justify-end">
              <a
                href={`https://wa.me/7054986771?text=${encodeURIComponent(`I'd like to schedule a free consultation on ${product.title}.`)}`}
                target="_blank" rel="noreferrer"
                data-testid="product-cta-whatsapp"
                className="btn-brand"
              >
                <WhatsappLogo size={16} weight="fill" /> {t("cta.consult")}
              </a>
              <button data-testid="product-cta-pilot" onClick={() => setGateOpen(true)} className="btn-ghost">
                <Rocket size={16} weight="bold" /> {t("cta.pilot")}
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsappFAB />
      <MobileStickyCTA />
      <TawkChat />
      {gateOpen && (
        <DemoGate product={product} onClose={() => setGateOpen(false)} onUnlocked={onUnlocked} />
      )}
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <div className="container-x pt-40 pb-32 text-center">
        <h1 className="font-display font-black text-4xl">Product not found</h1>
        <Link to="/#products" className="mt-6 inline-block btn-brand">Back to products</Link>
      </div>
      <Footer />
    </div>
  );
}

function DemoGate({ product, onClose, onUnlocked }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setLoading(true);
    try {
      const r = await api.post("/demo-request", { ...form, product_slug: product.slug });
      const stored = JSON.parse(localStorage.getItem("maxine_demo_tokens") || "{}");
      stored[product.slug] = r.data.demo_token;
      localStorage.setItem("maxine_demo_tokens", JSON.stringify(stored));
      toast.success("Access granted — opening demo");
      onUnlocked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="demo-gate" className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg border border-[color:var(--ink)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between border-b border-[color:var(--line)]">
          <div>
            <div className="eyebrow">Private demo</div>
            <div className="font-display font-bold text-lg">Unlock live {product.title}</div>
          </div>
          <button onClick={onClose} data-testid="demo-gate-close" className="p-2"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Your name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="demo-name" />
          <Field label="Work email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="demo-email" />
          <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="demo-company" />
          <p className="text-xs text-neutral-500">A short-lived link is created just for you — no account needed. We&apos;ll follow up on WhatsApp within one business day.</p>
          <button type="submit" disabled={loading} data-testid="demo-submit" className="btn-brand w-full justify-center">
            {loading ? "Unlocking…" : "Unlock demo"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
    </div>
  );
}
